import React, { useCallback } from 'react';
import { Stack, Text, Button, ModalOverlay } from '@moda/om';
import { formatPrice } from '../../../lib/formatPrice';
import { useOrderSummary } from '../../../hooks/useOrderSummary';
import { useChargeOrder } from '../../../hooks/useChargeOrder';
import { LoadingBalls } from '../../LoadingBalls';

import './PayTodayConfirmationModal.scss';

interface Props {
  onCancel: () => void;
  orderNumber: string;
  show: boolean;
}

export const PayTodayConfirmationModal: React.FC<Props> = ({ orderNumber, onCancel, show }) => {
  const { chargeOrder, loading } = useChargeOrder(orderNumber);
  const { orderSummary } = useOrderSummary(orderNumber);

  if (!orderSummary) return null;

  const {
    cardTypeName,
    paymentMethod,
    paymentSummary: { payToday, rewardsToBeUsed }
  } = orderSummary.attributes;

  const handleConfirm = useCallback(() => {
    chargeOrder().then(onCancel);
  }, [chargeOrder, onCancel]);

  return (
    <ModalOverlay className="PayTodayConfirmationModal" onClose={onCancel} show={show}>
      <Stack className="PayTodayConfirmationModal__content" alignItems="center" space={6}>
        <Text className="PayTodayConfirmationModal__text" family="serif" treatment="h5">
          This will charge {formatPrice(payToday.currency, payToday.price)} to your selected payment
          method(s) immediately. Do you wish to proceed?
        </Text>
        <Stack space={2}>
          <Text treatment="body2">Here is a breakdown of your payment:</Text>
          <ul>
            {Number(payToday.price) > 0 && (
              <li>
                <Text className="PayTodayConfirmationModal__text" treatment="body1">
                  {`${cardTypeName} ${paymentMethod.lastFour}`}:{' '}
                  {formatPrice(payToday.currency, payToday.price)}
                </Text>
              </li>
            )}

            {Number(rewardsToBeUsed.price) > 0 && (
              <li>
                <Text family="serif" treatment="body1">
                  M'O Rewards:
                  {formatPrice(rewardsToBeUsed.currency, rewardsToBeUsed.price)}
                </Text>
              </li>
            )}
          </ul>
        </Stack>

        <Text className="PayTodayConfirmationModal__text" family="serif" treatment="body2">
          *This change will not affect the estimated delivery date of your item(s).
        </Text>

        <Button onClick={handleConfirm}>Yes, pay remaining balance</Button>

        <Button secondary onClick={onCancel}>
          Cancel
        </Button>

        {loading && <LoadingBalls />}
      </Stack>
    </ModalOverlay>
  );
};
